import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { GenericEventFeed } from './GenericEventFeed';
import { useDatabaseEventFeedCardConfig, useResolvedSource } from './hooks';
import { useDatabaseEventsData } from '@/features/EventsListLayout/hooks';
import { useOpenInNewWindow } from '@/hooks';
import { SourceType } from '@/types/source';
import { AppRoutes } from '@/constants/routes';
import { buildDatabaseEventWatchUrl } from '@/utils/database-event-label';
import type { FeedEmptyStateProps } from './components';
import {
    ANALYZING_REFRESH_INTERVAL_MS,
    LIVE_REFRESH_INTERVAL_MS,
} from './constants';

interface DatabaseEventsFeedProps {
    // Scoped to a single source when provided; otherwise the general
    // directory feed aggregating identity events across all sources.
    sourceId?: string;
    titleContent?: ReactNode;
    emptyState?: FeedEmptyStateProps;
}

export function DatabaseEventsFeed({
    sourceId,
    titleContent,
    emptyState,
}: DatabaseEventsFeedProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const navigate = useNavigate();
    const { openUrl } = useOpenInNewWindow();
    const cardConfig = useDatabaseEventFeedCardConfig();

    const { resolvedSource, sourceReady, sourceIsAnalyzing } =
        useResolvedSource(sourceId);
    // The general directory feed spans cameras and files, so treat it as live
    // and keep it refreshing alongside websocket-driven updates.
    const isLive = sourceId ? resolvedSource?.type === SourceType.CAMERA : true;

    const refreshInterval = useMemo(() => {
        if (!sourceId) return LIVE_REFRESH_INTERVAL_MS;
        if (!sourceReady) return 0;
        if (sourceIsAnalyzing) return ANALYZING_REFRESH_INTERVAL_MS;
        if (isLive) return LIVE_REFRESH_INTERVAL_MS;
        return 0;
    }, [sourceId, sourceReady, sourceIsAnalyzing, isLive]);

    const { events, isLoading, isLoadingMore, hasMore, loadMore } =
        useDatabaseEventsData({ sourceId, refreshInterval });

    return (
        <GenericEventFeed
            {...cardConfig}
            isLive={isLive}
            containerTitle={t('sidebar.databaseEvents')}
            titleContent={titleContent}
            onViewAll={() =>
                navigate(
                    sourceId
                        ? `${AppRoutes.DATABASE_EVENTS}?sources=${sourceId}`
                        : AppRoutes.DATABASE_EVENTS,
                )
            }
            items={events}
            isLoading={isLoading}
            emptyState={emptyState}
            itemSelectPayload={(databaseEvent) => databaseEvent.event}
            onNavigate={(databaseEvent, clickEvent) =>
                openUrl(buildDatabaseEventWatchUrl(databaseEvent), clickEvent)
            }
            quickViewContextType="event-feed"
            pagination={{ hasMore, isLoadingMore, onLoadMore: loadMore }}
        />
    );
}
